import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, Truck, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface Carrier {
  id: string;
  name: string;
  code: string | null;
  trackingUrlTemplate: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  website: string | null;
  notes: string | null;
  status: string;
  isActive: boolean;
}

export default function CarriersSettingsPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "", code: "", trackingUrlTemplate: "",
    contactName: "", contactPhone: "", contactEmail: "",
    website: "", notes: "", isActive: true,
  });

  const { data: carriers = [] } = useQuery<Carrier[]>({
    queryKey: ["/api/carriers", { includeInactive: true }],
    queryFn: () => api.get<Carrier[]>(`/carriers?includeInactive=true`, sessionId),
  });

  const saveMutation = useMutation({
    mutationFn: () => editingId
      ? api.patch(`/carriers/${editingId}`, form, sessionId)
      : api.post(`/carriers`, form, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/carriers"] });
      setDialogOpen(false);
      setEditingId(null);
      toast({ title: editingId ? "Carrier updated" : "Carrier created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/carriers/${id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/carriers"] });
      toast({ title: "Carrier deleted" });
    },
  });

  const handleEdit = (c: Carrier) => {
    setEditingId(c.id);
    setForm({
      name: c.name, code: c.code || "",
      trackingUrlTemplate: c.trackingUrlTemplate || "",
      contactName: c.contactName || "", contactPhone: c.contactPhone || "",
      contactEmail: c.contactEmail || "", website: c.website || "",
      notes: c.notes || "", isActive: c.status === "active",
    });
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setForm({
      name: "", code: "", trackingUrlTemplate: "",
      contactName: "", contactPhone: "", contactEmail: "",
      website: "", notes: "", isActive: true,
    });
    setDialogOpen(true);
  };

  const handleDelete = (c: Carrier) => {
    if (confirm(`Delete carrier "${c.name}"?`)) {
      deleteMutation.mutate(c.id);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Carriers"
        description="Manage shipping carriers and their tracking URL templates"
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={handleNew} data-testid="button-new-carrier">
              <Plus className="h-4 w-4 mr-1" /> Add Carrier
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Truck className="h-4 w-4" /> All Carriers ({carriers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {carriers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No carriers configured.</p>
          ) : (
            <div className="space-y-2">
              {carriers.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/40"
                  data-testid={`carrier-row-${c.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {c.code && <span className="font-mono text-xs font-medium text-muted-foreground">{c.code}</span>}
                      <span className="font-medium text-sm">{c.name}</span>
                      {c.status !== "active" && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                    </div>
                    {c.contactPhone || c.contactEmail || c.website ? (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {[c.contactName, c.contactPhone, c.contactEmail, c.website].filter(Boolean).join(" · ")}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(c)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(c)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Carrier" : "New Carrier"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Name *</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="UPS"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Code</label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="UPS"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Tracking URL Template</label>
              <Input
                value={form.trackingUrlTemplate}
                onChange={(e) => setForm({ ...form, trackingUrlTemplate: e.target.value })}
                placeholder="https://www.ups.com/track?tracknum={tracking_number}"
              />
              <p className="text-xs text-muted-foreground mt-1">Use <code>{"{tracking_number}"}</code> as the placeholder</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Contact Name</label>
                <Input value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Contact Phone</label>
                <Input value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Contact Email</label>
                <Input value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Website</label>
                <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Active
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={!form.name || saveMutation.isPending}
              >
                {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingId ? "Save Changes" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
